import React, { useRef, useState, useEffect } from 'react';
import Image from 'next/legacy/image';

import { CFColorTokenProp } from '../../../theme/tokens/contentful-color';

import { BannerMessage } from '../BannerMessage';
import {
  GetStartedInput,
  GetStartedButton,
  GetStartedIconBtn,
  GetStartedWrapper,
  Error
} from './GetStartedWidget.styles';
import { Widget } from '../styles';

import { WidgetProps, DEFAULT_BUTTON_TEXT, SharedProps } from '../shared';
import RichText from '~/components/RichText';

import blackMiniArrow from '@/images/mini-arrow-black.svg';
import whiteMiniArrow from '@/images/mini-arrow-white.svg';
import blackArrow from '@/images/arrow-black.svg';
import whiteArrow from '@/images/arrow-white.svg';
import { Block, BLOCKS, Inline } from '@contentful/rich-text-types';
import { RenderNode } from '@contentful/rich-text-react-renderer';

export interface GetStartedWidgetProps extends WidgetProps {
  onStartClick?: () => void;
}

const ctaOverrideOptions: RenderNode = {
  [BLOCKS.PARAGRAPH]: (_node: Block | Inline, children) => {
    return (
      <p className="w-fit items-center justify-center text-[10px] leading-4">
        {children}
      </p>
    );
  }
};

export function GetStartedWidget({
  autocompleteAttribute,
  buttonColor = 'ginger',
  buttonHoverColor = 'green',
  buttonText = DEFAULT_BUTTON_TEXT,
  buttonContent,
  isScrolled = false,
  label,
  privacyDisclaimerDropdown,
  errorMessage,
  errorPosition,
  showErrorBanner,
  inputProps
}: WidgetProps & SharedProps) {
  const [isStartClicked, setIsStartClicked] = useState(false);
  const [imgSrc, setImgSrc] = useState('');

  const getStartInputRef = useRef<any>();
  const tabIndex = isStartClicked ? 0 : -1;

  useEffect(() => {
    const useLightArrow = ['blackberry', 'sweetmint'].includes(
      buttonColor as string
    );
    const arrow = useLightArrow ? whiteArrow : blackArrow;
    const miniArrow = useLightArrow ? whiteMiniArrow : blackMiniArrow;
    setImgSrc(isScrolled ? miniArrow : arrow);
  }, [buttonColor]);

  return isStartClicked ? (
    <GetStartedWrapper
      id="flex-wrapper"
      flexDirection="column"
      justifyContent="center"
    >
      <Widget alignItems="center">
        <GetStartedInput
          {...inputProps}
          ref={getStartInputRef}
          tabIndex={tabIndex}
          isScrolled={isScrolled}
          placeholder={label ?? ''}
          autoComplete={autocompleteAttribute ?? 'tel'}
          borderColor={errorMessage ? '#D31400' : '#3BBFAD'}
        />
        <GetStartedIconBtn
          type="submit"
          cfButtonColor={buttonColor as CFColorTokenProp}
          cfButtonHoverColor={buttonHoverColor as CFColorTokenProp}
          isScrolled={isScrolled}
          aria-label="Get started"
          data-testid="get-started-button"
        >
          <Image src={imgSrc} alt="Get started with Greenlight" />
        </GetStartedIconBtn>
      </Widget>
      <div className="absolute left-0 top-full h-8 w-full overflow-hidden rounded-b-lg transition-all duration-300">
        <div className="flex justify-center items-center h-full">
          <RichText
            data={privacyDisclaimerDropdown}
            overrideOptions={ctaOverrideOptions}
          />
        </div>
      </div>

      {showErrorBanner && errorMessage && !privacyDisclaimerDropdown && (
        <Error position={errorPosition} isScrolled={isScrolled}>
          {errorMessage}
        </Error>
      )}
      {showErrorBanner && errorMessage && !!privacyDisclaimerDropdown && (
        <BannerMessage
          message={errorMessage}
          position={errorPosition}
          isDisclaimerPresent={!!privacyDisclaimerDropdown}
          variant="error"
        />
      )}
    </GetStartedWrapper>
  ) : (
    <GetStartedButton
      type="submit"
      cfButtonColor={buttonColor as CFColorTokenProp}
      cfButtonHoverColor={buttonHoverColor as CFColorTokenProp}
      isScrolled={isScrolled}
      onClick={(e) => {
        e.preventDefault();
        setIsStartClicked(true);
        // onStartClick && onStartClick();
        return getStartInputRef?.current?.element?.focus();
      }}
      aria-label="Get started"
      data-testid="get-started-button"
    >
      {buttonContent || buttonText}
    </GetStartedButton>
  );
}
